import { useState } from "react";
import { Globe, Mail, MapPin } from 'lucide-react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import SEO from '../components/SEO';

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const center = { lat: 55.6122406, lng: 12.9995841 }; // Hans Michelsensgatan 9, Malmö
  const { isLoaded } = useLoadScript({ googleMapsApiKey: apiKey || '' });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <SEO page="contact" />
      <div className="container mx-auto px-4 py-16">
      {/* Header */}
      <section className="max-w-5xl mx-auto text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-primary-text">Contact Us</h1>
        <p className="text-lg md:text-xl text-secondary-text">We’re here to help with partnerships, support, and general inquiries.</p>
      </section>

      {/* Info Grid */}
      <section className="max-w-6xl mx-auto mb-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group rounded-lg bg-card-bg border border-card-border p-6 card-hover">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-5 h-5 text-primary-text" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-primary-text">Email</h3>
          </div>
          <p className="text-secondary-text mb-2">info@kreatives.app</p>
          <a className="nav-link hover:underline underline-offset-4" href="mailto:info@kreatives.app">Send an email</a>
        </div>
        <div className="group rounded-lg bg-card-bg border border-card-border p-6 card-hover">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-5 h-5 text-primary-text" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-primary-text">Website</h3>
          </div>
          <a className="nav-link mb-2 hover:underline underline-offset-4" href="https://kreatives.app" target="_blank" rel="noreferrer">https://kreatives.app</a>
          <p className="text-secondary-text">Visit our homepage</p>
        </div>
        <div className="group rounded-lg bg-card-bg border border-card-border p-6 card-hover">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-5 h-5 text-primary-text" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-primary-text">Address</h3>
          </div>
          <p className="text-secondary-text">Hans Michelsensgatan 9, 211 20 Malmö, Sweden</p>
          <a className="nav-link hover:underline underline-offset-4" href="https://www.google.com/maps?q=Hans+Michelsensgatan+9,+Malmö" target="_blank" rel="noreferrer">Open in Google Maps</a>
        </div>
      </section>

      {/* Map */}
      <section className="max-w-6xl mx-auto mb-16">
        <div className="rounded-lg overflow-hidden border border-card-border bg-card-bg">
          {apiKey && isLoaded ? (
            <GoogleMap
              center={center}
              zoom={15}
              mapContainerClassName="h-72 md:h-96 w-full"
              options={{ mapTypeControl: false, streetViewControl: false }}
            >
              <Marker position={center} />
            </GoogleMap>
          ) : (
            <div className="h-72 md:h-96 bg-secondary-bg flex items-center justify-center">
              <div className="text-center">
                <p className="text-primary-text">Map unavailable</p>
                <p className="text-secondary-text text-sm">Set `VITE_GOOGLE_MAPS_API_KEY` to enable the map.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Form */}
      <section className="max-w-6xl mx-auto">
        <div className="rounded-lg border border-card-border bg-card-bg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-primary-text">Send us a message</h2>
          {submitted ? (
            <div className="rounded-md bg-secondary-bg p-4">
              <p className="text-primary-text">Thanks! Your message has been sent. We’ll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-primary-text mb-1">Your Name</label>
                  <input id="name" className="w-full rounded-md bg-input-bg border border-card-border px-3 py-2 text-primary-text placeholder-secondary-text focus:outline-none" name="name" value={form.name} onChange={onChange} type="text" placeholder="John Doe" required />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-1">Email</label>
                  <input id="email" className="w-full rounded-md bg-input-bg border border-card-border px-3 py-2 text-primary-text placeholder-secondary-text focus:outline-none" name="email" value={form.email} onChange={onChange} type="email" placeholder="you@example.com" required />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-primary-text mb-1">Subject</label>
                <input id="subject" className="w-full rounded-md bg-input-bg border border-card-border px-3 py-2 text-primary-text placeholder-secondary-text focus:outline-none" name="subject" value={form.subject} onChange={onChange} type="text" placeholder="How can we help?" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-primary-text mb-1">Message</label>
                <textarea id="message" className="w-full rounded-md bg-input-bg border border-card-border px-3 py-2 text-primary-text placeholder-secondary-text focus:outline-none" name="message" value={form.message} onChange={onChange} rows={5} placeholder="Write your message here…" required />
              </div>
              <div className="flex justify-end">
                <button className="cta-brand inline-flex items-center justify-center px-6 py-3" data-variant="primary" type="submit">Send</button>
              </div>
            </form>
          )}
        </div>
      </section>
      </div>
    </>
  );
}
